import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Booking } from '../../types/booking';
import { apiService } from '../../services/api';
import { Toast } from '../../components/common/Toast';

type SortField = 'date' | 'status';
type SortDirection = 'asc' | 'desc';
type StatusFilter = 'all' | 'pending' | 'confirmed' | 'cancelled';

export function IntervieweeBookings() {
  const { t } = useTranslation();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  
  // Filtering and sorting state
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [searchQuery, setSearchQuery] = useState('');

  const loadBookings = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.bookings.listForInterviewee();
      setBookings(data);
    } catch (_err) {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBookings();
  }, []);

  const handleCancelBooking = async (bookingId: number) => {
    try {
      await apiService.bookings.cancel(bookingId.toString());
      setNotification({
        message: t('bookings.cancelled'),
        type: 'success',
      });
      loadBookings();
    } catch (_err) {
      setNotification({
        message: t('common.error'),
        type: 'error',
      });
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredAndSortedBookings = useMemo(() => {
    let result = [...bookings];
    
    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter(booking => booking.status === statusFilter);
    }
    
    // Filter by search query (interviewer name/company)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(booking => 
        booking.interviewerName?.toLowerCase().includes(query) || 
        booking.interviewerCompany?.toLowerCase().includes(query) || false
      );
    }
    
    // Then sort
    return [...result].sort((a, b) => {
      if (sortField === 'date') {
        const dateA = new Date(a.startTime).getTime();
        const dateB = new Date(b.startTime).getTime();
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      } else if (sortField === 'status') {
        const statusOrder = { pending: 0, confirmed: 1, cancelled: 2 };
        const statusA = statusOrder[a.status as keyof typeof statusOrder];
        const statusB = statusOrder[b.status as keyof typeof statusOrder];
        return sortDirection === 'asc' ? statusA - statusB : statusB - statusA;
      }
      return 0;
    });
  }, [bookings, statusFilter, sortField, sortDirection, searchQuery]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-stitch-muted">{t('common.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center px-4">
        <div className="max-w-md rounded-stitch-lg border border-stitch-danger/40 bg-stitch-danger-soft px-4 py-3 text-center text-stitch-fg-soft">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <h2 className="mb-6 text-xl font-bold text-stitch-fg md:text-2xl">{t('bookings.myBookings')}</h2>

      {/* Filters and Search */}
      <div className="mb-6 rounded-stitch-xl border border-stitch-border bg-stitch-surface p-5 shadow-stitch-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div>
              <label htmlFor="status-filter" className="block text-sm font-medium text-stitch-fg-soft mb-1">
                {t('bookings.filterByStatus')}
              </label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="block w-full rounded-stitch-lg border-stitch-border shadow-sm focus:border-stitch-primary focus:ring-stitch-primary"
              >
                <option value="all">{t('bookings.allStatuses')}</option>
                <option value="pending">{t('bookings.status.pending')}</option>
                <option value="confirmed">{t('bookings.status.confirmed')}</option>
                <option value="cancelled">{t('bookings.status.cancelled')}</option>
              </select>
            </div>
            
            <div>
              <label htmlFor="sort-by" className="block text-sm font-medium text-stitch-fg-soft mb-1">
                {t('bookings.sortBy')}
              </label>
              <div className="flex">
                <select
                  id="sort-by"
                  value={sortField}
                  onChange={(e) => handleSort(e.target.value as SortField)}
                  className="block w-full rounded-l-stitch-lg border-stitch-border shadow-sm focus:border-stitch-primary focus:ring-stitch-primary"
                >
                  <option value="date">{t('bookings.date')}</option>
                  <option value="status">{t('bookings.status.label')}</option>
                </select>
                <button
                  onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
                  className="flex items-center rounded-r-stitch-lg border border-stitch-border bg-stitch-surface-2 px-2"
                >
                  {sortDirection === 'asc' ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
          </div>
          
          <div className="flex-1 max-w-md">
            <label htmlFor="search" className="block text-sm font-medium text-stitch-fg-soft mb-1">
              {t('bookings.searchByInterviewer')}
            </label>
            <input
              id="search"
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('bookings.searchInterviewerPlaceholder')}
              className="block w-full rounded-stitch-lg border-stitch-border shadow-sm focus:border-stitch-primary focus:ring-stitch-primary"
            />
          </div>
        </div>
      </div>

      {/* Bookings List */}
      {filteredAndSortedBookings.length === 0 ? (
        <div className="rounded-stitch-xl border border-dashed border-stitch-border bg-stitch-surface p-8 text-center shadow-stitch-card">
          <p className="text-stitch-muted">{t('bookings.noBookings')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredAndSortedBookings.map((booking) => (
            <div
              key={booking.id}
              className="rounded-stitch-xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-card"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className="text-lg font-semibold text-stitch-fg">
                      {booking.interviewerName || 'Interviewer'}
                    </h3>
                    <span className={`ml-3 rounded-full px-2 py-1 text-xs font-medium ${
                      booking.status === 'confirmed'
                        ? 'bg-stitch-success-soft text-stitch-success'
                        : booking.status === 'pending'
                          ? 'bg-stitch-warning-soft text-stitch-warning'
                          : 'bg-stitch-danger-soft text-stitch-danger'
                    }`}>
                      {t(`bookings.status.${booking.status}`)}
                    </span>
                  </div>
                  
                  <div className="text-sm text-stitch-muted space-y-1">
                    <p>
                      <strong>{t('bookings.date')}:</strong>{' '}
                      {new Date(booking.startTime).toLocaleDateString('en-GB', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric' 
                      })}
                    </p>
                    <p>
                      <strong>{t('bookings.time')}:</strong>{' '}
                      {new Date(booking.startTime).toLocaleTimeString()} - {new Date(booking.endTime).toLocaleTimeString()}
                    </p>
                    {booking.interviewerSpecialty && (
                      <p>
                        <strong>{t('bookings.specialty')}:</strong> {booking.interviewerSpecialty}
                      </p>
                    )}
                    {booking.interviewerCompany && (
                      <p>
                        <strong>{t('bookings.company')}:</strong> {booking.interviewerCompany}
                      </p>
                    )}
                    {booking.meetingLink && (
                      <p>
                        <strong>{t('bookings.meetingLink')}:</strong>{' '}
                        <a 
                          href={booking.meetingLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="break-all font-medium text-stitch-primary underline hover:text-stitch-primary-hover"
                        >
                          {booking.meetingLink}
                        </a>
                      </p>
                    )}
                    {booking.notes && (
                      <p>
                        <strong>{t('bookings.notes')}:</strong> {booking.notes}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="mt-4 lg:mt-0 lg:ml-6 flex flex-col sm:flex-row gap-2">
                  {booking.status === 'pending' && (
                    <button
                      onClick={() => handleCancelBooking(booking.id)}
                      className="rounded-stitch-md bg-stitch-danger px-4 py-2 text-sm font-medium text-white transition hover:opacity-90"
                    >
                      {t('bookings.cancel')}
                    </button>
                  )}
                  
                  {booking.status === 'confirmed' && booking.meetingLink && (
                    <a
                      href={booking.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center rounded-stitch-md bg-stitch-success px-4 py-2 text-sm font-medium text-white transition hover:opacity-90"
                    >
                      {t('bookings.joinMeeting')}
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Notification Toast */}
      {notification && (
        <Toast
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}
    </div>
  );
}
